import React, { useState } from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import AlertDismissible from '../../Shared/Components/AlertDismissible'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import Layout from '../../Shared/Layout/LayoutAdmin'
import PaginationList from '../../Shared/Components/PaginationList'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Card from 'react-bootstrap/Card'
import { SIA_NAVY } from '../../constants/siaTheme'
import { canEditSecomRecords } from '../../utils/secomPermissions'

function formatDate(val) {
    if (!val) return '—'
    const d = String(val).slice(0, 10)
    const [y, m, day] = d.split('-')
    if (!y || !m || !day) return val
    return `${day}/${m}/${y}`
}

function formatMoney(val) {
    const n = Number(val)
    if (Number.isNaN(n)) return '—'
    return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
}

function RecordCard({ row, canViewFicha, canEdit, onDelete }) {
    return (
        <Col md={6} xl={4} className="mb-6">
            <Card className="h-100 shadow-sm border border-gray-200">
                <Card.Header className="bg-light-primary border-0 pt-5 pb-4">
                    <Card.Title className="fs-6 fw-bold text-gray-900 text-uppercase mb-0">{row.insured_name}</Card.Title>
                </Card.Header>
                <Card.Body className="pt-4">
                    {canViewFicha && row.ficha_thumbnail_url ? (
                        <div className="text-center mb-4">
                            <img
                                src={row.ficha_thumbnail_url}
                                alt="Vista previa ficha"
                                className="rounded border"
                                style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain' }}
                            />
                        </div>
                    ) : canViewFicha && row.has_ficha ? (
                        <div className="text-center mb-4 py-6 bg-light rounded">
                            <i className="bi bi-file-earmark-pdf fs-2x text-danger" />
                            <div className="text-muted fs-8 mt-2">Documento adjunto</div>
                        </div>
                    ) : null}

                    <dl className="mb-0 fs-7">
                        <dt className="text-gray-600 fw-semibold">Asesor asignado</dt>
                        <dd className="text-gray-800 mb-3">{row.adviser_display_name}</dd>
                        <dt className="text-gray-600 fw-semibold">Póliza</dt>
                        <dd className="text-gray-800 mb-3">{row.policy_number}</dd>
                        <dt className="text-gray-600 fw-semibold">Status</dt>
                        <dd className="text-gray-800 mb-3">{row.status_name}</dd>
                        <dt className="text-gray-600 fw-semibold">Vigencia</dt>
                        <dd className="text-gray-800 mb-3">{formatDate(row.validity_date)}</dd>
                        <dt className="text-gray-600 fw-semibold">Suma asegurada</dt>
                        <dd className="text-gray-800 mb-3">{formatMoney(row.insured_amount)}</dd>
                        <dt className="text-gray-600 fw-semibold">Desc. Qnal.</dt>
                        <dd className="text-gray-800 mb-3">{formatMoney(row.biweekly_discount)}</dd>
                    </dl>

                    {row.action && (
                        <div className="mb-3">
                            <div className="text-gray-600 fw-semibold fs-8 text-uppercase mb-1">Acciones a realizar</div>
                            <p className="text-gray-700 fs-8 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                {row.action}
                            </p>
                        </div>
                    )}

                    {row.results && (
                        <div className="mb-3">
                            <div className="text-gray-600 fw-semibold fs-8 text-uppercase mb-1">Resultados</div>
                            <p className="text-gray-700 fs-8 mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                {row.results}
                            </p>
                        </div>
                    )}
                </Card.Body>
                <Card.Footer className="border-0 pt-0 pb-5 d-flex flex-wrap gap-2 justify-content-center">
                    {canViewFicha && row.has_ficha && (
                        <a
                            href={route('secom.ficha', row.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-success"
                        >
                            <i className="bi bi-eye me-1" />
                            Ver ficha
                        </a>
                    )}
                    {canEdit && (
                        <>
                            <Link href={route('secom.edit', row.id)} className="btn btn-sm btn-primary">
                                <i className="bi bi-pencil me-1" />
                                Editar
                            </Link>
                            <Button variant="light-danger" size="sm" onClick={() => onDelete(row)}>
                                <i className="bi bi-trash me-1" />
                                Eliminar
                            </Button>
                        </>
                    )}
                </Card.Footer>
            </Card>
        </Col>
    )
}

const SecomIndex = ({ entries, filters, offices, advisers, statuses, canEdit, canViewFicha }) => {
    const { flash } = usePage().props
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleting, setDeleting] = useState(false)

    const filterForm = useForm({
        search: filters.search || '',
        office_id: filters.office_id || '',
        adviser_id: filters.adviser_id || '',
        secom_status_id: filters.secom_status_id || '',
    })

    const submitFilters = (e) => {
        e.preventDefault()
        filterForm.get(route('secom.index'), { preserveState: true })
    }

    const clearFilters = () => {
        filterForm.setData({ search: '', office_id: '', adviser_id: '', secom_status_id: '' })
        filterForm.get(route('secom.index'))
    }

    const confirmDelete = () => {
        if (!deleteTarget) return
        setDeleting(true)
        Inertia.delete(route('secom.destroy', deleteTarget.id), {
            preserveScroll: true,
            onFinish: () => {
                setDeleting(false)
                setDeleteTarget(null)
            },
        })
    }

    const data = entries.data || []
    const showEdit = canEdit || canEditSecomRecords()

    return (
        <Row>
            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            <span className="pb-7 pt-7">
                <Breadcrumbs currentPath="admin/secom/registros" />
            </span>

            <div
                className="rounded-2 mb-8 py-4 px-4 text-center text-white text-uppercase fw-bold fs-4"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.04em' }}
            >
                Ver registros — SECOM
            </div>

            <div className="card card-flush shadow-sm mb-8">
                <div className="card-body py-6">
                    <Form onSubmit={submitFilters} className="row g-3 align-items-end">
                        <Col md={3}>
                            <Form.Label className="fw-bold fs-7">Buscar</Form.Label>
                            <Form.Control
                                type="search"
                                placeholder="Asegurado, RFC, póliza…"
                                value={filterForm.data.search}
                                onChange={(e) => filterForm.setData('search', e.target.value)}
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label className="fw-bold fs-7">Oficina</Form.Label>
                            <Form.Select
                                value={filterForm.data.office_id}
                                onChange={(e) => filterForm.setData('office_id', e.target.value)}
                            >
                                <option value="">Todas</option>
                                {offices.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        {o.nombre}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="fw-bold fs-7">Asesor</Form.Label>
                            <Form.Select
                                value={filterForm.data.adviser_id}
                                onChange={(e) => filterForm.setData('adviser_id', e.target.value)}
                            >
                                <option value="">Todos</option>
                                {advisers.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.display_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Form.Label className="fw-bold fs-7">Status</Form.Label>
                            <Form.Select
                                value={filterForm.data.secom_status_id}
                                onChange={(e) => filterForm.setData('secom_status_id', e.target.value)}
                            >
                                <option value="">Todos</option>
                                {statuses.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col md={2} className="d-flex gap-2">
                            <Button type="submit" size="sm" variant="primary" disabled={filterForm.processing}>
                                Filtrar
                            </Button>
                            <Button type="button" size="sm" variant="light" onClick={clearFilters}>
                                Limpiar
                            </Button>
                        </Col>
                    </Form>
                </div>
            </div>

            {showEdit && (
                <div className="mb-6 text-end">
                    <Link href={route('secom.create')} className="btn btn-sm btn-primary">
                        <i className="bi bi-plus-lg me-1" />
                        Agregar registro
                    </Link>
                </div>
            )}

            {data.length === 0 ? (
                <div className="text-center text-muted py-15">No hay registros para mostrar.</div>
            ) : (
                <Row>
                    {data.map((row) => (
                        <RecordCard
                            key={row.id}
                            row={row}
                            canViewFicha={canViewFicha}
                            canEdit={showEdit}
                            onDelete={setDeleteTarget}
                        />
                    ))}
                </Row>
            )}

            <PaginationList links={entries.links} filters={filters} />

            <Modal show={Boolean(deleteTarget)} onHide={() => !deleting && setDeleteTarget(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminar registro</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Eliminar el registro de <strong>{deleteTarget?.insured_name}</strong>? Esta acción no se puede deshacer.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    )
}

SecomIndex.layout = (page) => <Layout title="SECOM — Ver registros">{page}</Layout>

export default SecomIndex
